import { CONNECTION_STATUS_LABELS, PROVIDER_LABELS, type ConnectionProvider, type ConnectionStatus } from '@lia/contracts';
import { Alert02Icon, CancelCircleIcon, CheckmarkCircle02Icon, DashedLineCircleIcon, MinusSignCircleIcon, Plug01Icon } from '@hugeicons/core-free-icons';
import { Icon } from '../primitives/icon.js';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { formatRelative } from '../../lib/format.js';
import { StatusBadge } from '../primitives/badge.js';
import { Card } from '../primitives/card.js';
import { CONNECTION_STATUS_TONE } from './status.js';

export type CapabilityState = 'verified' | 'unverified' | 'unsupported' | 'failed';

export const CAPABILITY_STATE_LABELS: Record<CapabilityState, string> = {
  verified: 'Verificada',
  unverified: 'Sin verificar',
  unsupported: 'No disponible en esta cuenta',
  failed: 'Falló la verificación',
};

const CAPABILITY_ICON: Record<CapabilityState, React.ReactNode> = {
  verified: <Icon icon={CheckmarkCircle02Icon} aria-hidden className="text-success" />,
  unverified: <Icon icon={DashedLineCircleIcon} aria-hidden className="text-text-secondary" />,
  unsupported: <Icon icon={MinusSignCircleIcon} aria-hidden className="text-text-secondary" />,
  failed: <Icon icon={CancelCircleIcon} aria-hidden className="text-danger" />,
};

export interface ConnectionCapability {
  key: string;
  label: string;
  state: CapabilityState;
  /** Extra detail, e.g. missing permission name. */
  detail?: string;
}

export interface ConnectionCardProps {
  provider: ConnectionProvider;
  status: ConnectionStatus;
  /** Account / location / portal shown to the user, e.g. "Desarrolladora Norte (portal 0000001)". */
  accountName?: string | null;
  capabilities?: readonly ConnectionCapability[];
  /** Latest health check message (plain text, no secrets). */
  healthMessage?: string | null;
  lastCheckedAt?: string | null;
  /** Provider logo or icon. */
  icon?: React.ReactNode;
  /** Buttons: Conectar, Probar, Reconectar, Desconectar… */
  actions?: React.ReactNode;
  now?: Date;
  className?: string;
}

/** Provider connection with honest capability states (verified / unverified / unsupported / failed). */
export const ConnectionCard = React.forwardRef<HTMLDivElement, ConnectionCardProps>(function ConnectionCard(
  { provider, status, accountName, capabilities = [], healthMessage, lastCheckedAt, icon, actions, now, className },
  ref,
) {
  const headingId = React.useId();
  const healthTone = status === 'connected' ? 'text-text-secondary' : status === 'revoked' ? 'text-danger' : status === 'needs_attention' ? 'text-warning' : 'text-text-secondary';
  return (
    <Card ref={ref} as="article" aria-labelledby={headingId} className={cn('flex flex-col p-5', className)}>
      <div className="flex items-start gap-3">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-control bg-surface-subtle text-text-secondary [&_svg]:size-5">
          {icon ?? <Icon icon={Plug01Icon} aria-hidden />}
        </div>
        <div className="grid min-w-0 flex-1 gap-0.5">
          <div className="flex flex-wrap items-center gap-2">
            <h3 id={headingId} className="text-body-lg font-semibold text-text-primary">
              {PROVIDER_LABELS[provider]}
            </h3>
            <StatusBadge tone={CONNECTION_STATUS_TONE[status]}>{CONNECTION_STATUS_LABELS[status]}</StatusBadge>
          </div>
          <p className="truncate text-body text-text-secondary">{accountName ?? 'Sin cuenta conectada'}</p>
        </div>
      </div>
      {healthMessage ? (
        <p className={cn('mt-3 flex items-start gap-1.5 text-body', healthTone)}>
          {status === 'needs_attention' || status === 'revoked' ? <Icon icon={Alert02Icon} aria-hidden className="mt-0.5 size-4 shrink-0" /> : null}
          <span>{healthMessage}</span>
        </p>
      ) : null}
      {capabilities.length > 0 ? (
        <div className="mt-4">
          <p className="mb-1.5 text-meta font-medium text-text-secondary">Capacidades</p>
          <ul className="grid gap-1.5">
            {capabilities.map((c) => (
              <li key={c.key} className="flex items-start gap-2 text-body [&_svg]:size-4 [&_svg]:shrink-0">
                <span className="mt-0.5">{CAPABILITY_ICON[c.state]}</span>
                <span className="min-w-0 flex-1">
                  <span className={cn('text-text-primary', c.state === 'unsupported' && 'text-text-secondary')}>{c.label}</span>
                  <span className="text-text-secondary"> · {CAPABILITY_STATE_LABELS[c.state]}</span>
                  {c.detail ? <span className="block text-meta text-text-secondary">{c.detail}</span> : null}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {lastCheckedAt || actions ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-border-subtle pt-3">
          <span className="text-meta text-text-secondary">
            {lastCheckedAt ? `Última verificación ${formatRelative(lastCheckedAt, now)}` : 'Nunca verificada'}
          </span>
          {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
        </div>
      ) : null}
    </Card>
  );
});
